/**
 * Product Purchase Options
 * Mirrors the cart-drawer membership add flow exactly:
 * - Adds membership + welcome gift (if not already in cart)
 * - Uses same renderContents/refreshMembershipDrawer pattern
 * - Hides membership option for existing greenhouse members
 * - Updates ATC button text with quantity
 */

(function () {
  const MEMBER_DISCOUNT_PERCENT = 10;

  function resolveConfig(el) {
    // Prefer EG_CART_DRAWER_MEMBERSHIP (already on page via cart-drawer.liquid)
    const d = window.EG_CART_DRAWER_MEMBERSHIP || {};
    return {
      membershipVariantId: d.membershipVariantId || parseInt(el.dataset.membershipVariantId, 10),
      sellingPlanId: d.membershipSellingPlanId || parseInt(el.dataset.membershipSellingPlanId, 10),
      giftVariantId: d.membershipGiftVariantId || parseInt(el.dataset.membershipGiftVariantId, 10),
      giftPropertyKey: d.membershipGiftPropertyKey || el.dataset.membershipGiftPropertyKey,
      giftPropertyValue: d.membershipGiftPropertyValue || el.dataset.membershipGiftPropertyValue,
      membershipProductId: d.membershipProductId || el.dataset.membershipProductId,
      membershipItemVariantId: d.membershipItemVariantId || parseInt(el.dataset.membershipItemVariantId, 10),
      drawerSections: d.drawerSections || 'cart-drawer,cart-icon-bubble',
    };
  }

  /**
   * Resolve the currently selected variant ID from bundle-picker radios.
   * Falls back to the hidden product-variant-id input in our form.
   */
  function resolveVariantId(el) {
    const selectedVariant = resolveSelectedVariant(el);
    if (selectedVariant) return selectedVariant.id;

    const input = el.querySelector('.product-variant-id');
    return input ? parseInt(input.value, 10) : null;
  }

  function resolveSelectedVariant(el) {
    const bundleRoot = document.querySelector('.bundles-container');
    if (bundleRoot) {
      const variantsJsonEl = document.getElementById('product-variants-json');
      if (variantsJsonEl) {
        try {
          const variants = JSON.parse(variantsJsonEl.textContent);
          const o1 = bundleRoot.querySelector('input[name="option1"]:checked');
          const o2 = bundleRoot.querySelector('input[name="option2"]:checked');
          let v = null;
          if (o1 && o2) v = variants.find(x => x.option1 === o1.value && x.option2 === o2.value);
          else if (o1) v = variants.find(x => x.option1 === o1.value);
          if (v) return v;
        } catch (e) {}
      }
    }

    const input = el.querySelector('.product-variant-id');
    if (!input) return null;

    const variantsJsonEl = document.getElementById('product-variants-json');
    if (variantsJsonEl) {
      try {
        const variants = JSON.parse(variantsJsonEl.textContent);
        return variants.find((variant) => variant.id === parseInt(input.value, 10)) || null;
      } catch (e) {}
    }

    return null;
  }

  function resolveVariantPreorderState(el, variant) {
    const statesElement = el.querySelector('[id^="preorder-variant-states-"]');
    if (!statesElement || !variant?.id) return { active: el.dataset.customPreorder === 'true', timeline: '' };
    try {
      const states = JSON.parse(statesElement.textContent);
      return states[String(variant.id)] || { active: false, timeline: '' };
    } catch (_) {
      return { active: false, timeline: '' };
    }
  }

  function resolveVirtualBundlePreorderState(el, variantId) {
    const configElement = document.getElementById('virtual-bundle-config');
    if (!configElement || !variantId) return { active: false, timeline: '' };
    try {
      const config = JSON.parse(configElement.textContent);
      return config?.singleProductPreorderStates?.[String(variantId)] || { active: false, timeline: '' };
    } catch (_) {
      return { active: false, timeline: '' };
    }
  }

  function resolvePurchaseTarget(el) {
    const isCustomPreorder = el.dataset.customPreorder === 'true';
    const selectedVariant = resolveSelectedVariant(el);
    const preorderState = resolveVariantPreorderState(el, selectedVariant);
    const defaultTarget = {
      variantId: selectedVariant?.id || resolveVariantId(el),
      quantityMultiplier: 1,
      isVirtualBundle: false,
      isPreorder: preorderState.active,
      isSoldOut: selectedVariant ? selectedVariant.available === false : el.dataset.inventorySoldOut === 'true',
    };
    const configElement = document.getElementById('virtual-bundle-config');
    if (!configElement) return defaultTarget;

    try {
      const config = JSON.parse(configElement.textContent);
      if (!config.isVirtualBundle || !Array.isArray(config.singleProductVariants)) return defaultTarget;

      const bundleRoot = document.querySelector('.bundles-container');
      const selectedColor = bundleRoot?.querySelector('input[name="option1"]:checked')?.value;
      const singleVariant = config.singleProductVariants.find((variant) => variant.option1 === selectedColor)
        || config.singleProductVariants.find((variant) => variant.available)
        || config.singleProductVariants[0];
      if (!singleVariant?.id) return defaultTarget;

      const form = el.querySelector('.form[data-type="add-to-cart-form"]');
      const isPurpleDotPreorder = form?.classList.contains('x-pd-atc-form--preorder')
        || !!form?.querySelector('#pd-waitlist-info');
      const linkedSingleInventoryIsEmpty = singleVariant.inventory_management === 'shopify'
        && Number(singleVariant.inventory_quantity) <= 0;
      const linkedSingleIsPreorder = singleVariant.inventory_policy === 'continue'
        && linkedSingleInventoryIsEmpty;

      return {
        variantId: singleVariant.id,
        quantityMultiplier: parseInt(config.bundleQuantity, 10) || 4,
        isVirtualBundle: true,
        isPreorder: resolveVirtualBundlePreorderState(el, singleVariant.id).active
          || isPurpleDotPreorder
          || linkedSingleIsPreorder,
        isSoldOut: linkedSingleInventoryIsEmpty && singleVariant.inventory_policy !== 'continue',
      };
    } catch (error) {
      console.error('[PurchaseOptions] Virtual bundle configuration is invalid:', error);
      return defaultTarget;
    }
  }

  function resolveLineItemProperties(el) {
    const properties = {};
    const form = el.querySelector('.form[data-type="add-to-cart-form"]');
    if (!form) return properties;

    form.querySelectorAll('[name^="properties["]').forEach((input) => {
      if (input.disabled || !input.value) return;
      const match = input.name.match(/^properties\[(.+)\]$/);
      if (match) properties[match[1]] = input.value;
    });

    return properties;
  }

  async function withRegiosBedGift(items, sourceElement) {
    const purchaseOptions = sourceElement?.closest?.('.product-purchase-options');
    const productForm = sourceElement?.closest?.('product-form[data-product-handle]')
      || purchaseOptions?.querySelector?.('product-form[data-product-handle]');
    return window.EGRegiosBedGift?.prepareItems
      ? window.EGRegiosBedGift.prepareItems(items, {
          productHandle: productForm?.dataset.productHandle,
          productId: productForm?.dataset.productId,
        })
      : items;
  }

  async function getFreshCart() {
    const r = await fetch('/cart.js');
    return r.json();
  }

  function renderDrawer(state, triggerId) {
    const cartDrawer = document.querySelector('cart-drawer');
    if (!cartDrawer || typeof cartDrawer.renderContents !== 'function' || !state?.sections) return false;
    if (
      cartDrawer.classList.contains('active')
      && typeof window.refreshCartDrawerHotZones === 'function'
      && window.refreshCartDrawerHotZones(state, triggerId)
    ) return true;
    cartDrawer.renderContents({ sections: state.sections, id: triggerId });
    return true;
  }

  async function refreshDrawer(drawerSections, triggerId) {
    const r = await fetch(`/?sections=${drawerSections}`);
    const sections = await r.json();
    renderDrawer({ sections }, triggerId);
  }

  function parsePricingConfig(el) {
    const configElement = el.querySelector('[data-member-price-config]');
    if (!configElement) return null;
    try {
      return JSON.parse(configElement.textContent);
    } catch (_) {
      return null;
    }
  }

  function parseTierList(value) {
    if (typeof value === 'string') {
      try {
        value = JSON.parse(value);
      } catch (_) {
        return [];
      }
    }
    if (Array.isArray(value)) return value;
    if (value && typeof value === 'object') return [value];
    return [];
  }

  function isValidDate(value) {
    return typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value);
  }

  function pacificToday() {
    return new Date().toLocaleDateString('en-CA', { timeZone: 'America/Los_Angeles' });
  }

  function isLiveTier(tier, today) {
    const startsAt = tier.startsAt;
    const endsAt = tier.endsAt;
    if (startsAt != null && !isValidDate(startsAt)) return false;
    if (endsAt != null && !isValidDate(endsAt)) return false;
    if (startsAt && endsAt && startsAt > endsAt) return false;
    return !(startsAt && today < startsAt) && !(endsAt && today > endsAt);
  }

  function validNumber(value) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }

  function resolveBulkPercent(tiers, quantity, today) {
    return parseTierList(tiers)
      .map((tier) => {
        const minQty = validNumber(tier.minQty);
        const discount = validNumber(tier.discount);
        if (minQty == null || minQty < 1 || discount == null || discount <= 0 || !isLiveTier(tier, today)) return null;
        return { minQty, discount };
      })
      .filter(Boolean)
      .sort((a, b) => b.minQty - a.minQty)
      .find((tier) => quantity >= tier.minQty)?.discount || 0;
  }

  function resolveNonMemberPrice(config, variant, quantity) {
    if (!config || !variant) return null;
    const bulkPercent = resolveBulkPercent(config.bulkPricingTiers, quantity, pacificToday());
    const lineSubtotal = variant.price * quantity;
    if (bulkPercent <= 0) return lineSubtotal;

    // The discount Function rounds the discount once for the extended cart
    // line, not once per item. Mirror that calculation in the PDP preview.
    return Math.max(0, lineSubtotal - Math.round((lineSubtotal * bulkPercent) / 100));
  }

  function resolveMemberPrice(config, variant, quantity) {
    if (!config || !variant) return null;
    const today = pacificToday();
    const overrideSource = variant.memberDiscountOverride == null
      ? config.productOverride
      : variant.memberDiscountOverride;
    const overrides = parseTierList(overrideSource)
      .map((tier) => {
        const type = typeof tier.type === 'string' ? tier.type.trim().toLowerCase() : '';
        const value = validNumber(tier.value);
        const minQty = tier.minQty == null ? 1 : validNumber(tier.minQty);
        if (!['amount', 'percentage'].includes(type) || value == null || value <= 0 || minQty == null || minQty < 1 || !isLiveTier(tier, today)) return null;
        return { ...tier, type, value, minQty, perItem: tier.perItem === true, message: typeof tier.message === 'string' && tier.message.trim() ? tier.message.trim() : null };
      })
      .filter(Boolean)
      .sort((a, b) => b.minQty - a.minQty);
    const override = overrides.find((tier) => quantity >= tier.minQty);
    if (override) {
      const amountInCents = override.value * 100;
      const price = override.type === 'percentage'
        ? variant.price * (1 - Math.min(override.value, 100) / 100)
        : Math.max(0, variant.price - (override.perItem ? amountInCents : amountInCents / quantity));
      return { price: Math.round(price), promoMessage: override.message, source: 'override' };
    }
    const bulkPercent = resolveBulkPercent(config.bulkPricingTiers, quantity, today);
    const totalPercent = Math.min(bulkPercent + (config.isMemberEligible ? MEMBER_DISCOUNT_PERCENT : 0), 100);
    if (bulkPercent > 0) {
      const lineSubtotal = variant.price * quantity;
      return {
        price: Math.max(0, lineSubtotal - Math.round((lineSubtotal * totalPercent) / 100)),
        isLineTotal: true,
        promoMessage: null,
        source: 'standard',
      };
    }
    return { price: Math.round(variant.price * (1 - totalPercent / 100)), isLineTotal: false, promoMessage: null, source: 'standard' };
  }

  function formatMoney(cents) {
    const currency = window.Shopify?.currency?.active || 'USD';
    const hasCents = Math.round(cents) % 100 !== 0;
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency,
      minimumFractionDigits: hasCents ? 2 : 0,
      maximumFractionDigits: hasCents ? 2 : 0,
    }).format(cents / 100);
  }

  async function addSetupUpsell(button) {
    const variantId = parseInt(button.dataset.variantId, 10);
    if (!variantId) return;

    const originalText = button.textContent;
    button.disabled = true;
    button.textContent = 'Adding...';

    try {
      const drawerSections = 'cart-drawer,cart-icon-bubble';
      const r = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: await withRegiosBedGift([{
            id: variantId,
            quantity: 1,
            properties: {
              _upsell_source: 'epic_pdp_setup',
              _upsell_list: 'Complete Your Setup',
            },
          }], button),
          sections: drawerSections,
          sections_url: window.location.pathname,
        }),
      });

      if (!r.ok) {
        const e = await r.json();
        throw new Error(e.description || 'Failed');
      }

      const result = await r.json();
      if (!renderDrawer(result, variantId)) await refreshDrawer(drawerSections, variantId);

      button.textContent = 'Added';
      setTimeout(() => {
        button.textContent = originalText;
        button.disabled = false;
      }, 1800);
    } catch (err) {
      console.error('[EpicSetupUpsell] Add failed:', err);
      button.textContent = 'Try Again';
      button.disabled = false;
    }
  }

  function bindSetupUpsells() {
    if (document.documentElement.dataset.epicSetupUpsellsInit) return;
    document.documentElement.dataset.epicSetupUpsellsInit = '1';

    document.addEventListener('click', (event) => {
      const button = event.target.closest('[data-epic-setup-add]');
      if (!button) return;
      event.preventDefault();
      addSetupUpsell(button);
    });
  }

  class ProductPurchaseOptions {
    constructor(el) {
      this.el = el;
      this.sectionId = el.dataset.sectionId;
      this.cfg = resolveConfig(el);
      this.selectedOption = el.querySelector('.purchase-option__radio[value="membership"]:checked') ? 'membership' : 'onetime';
      const requestedPriceOption = new URLSearchParams(window.location.search).get('member-price');
      if (requestedPriceOption === 'membership') {
        const membershipRadio = el.querySelector('.purchase-option__radio[value="membership"]');
        if (membershipRadio) {
          membershipRadio.checked = true;
          this.selectedOption = 'membership';
        }
      }
      this.memberPriceConfig = parsePricingConfig(el);
      this.memberPriceOptions = el.querySelector('[data-member-price-options]');
      this.signedInMemberPrice = el.querySelector('[data-signed-in-member-price]');
      this.memberPriceSlot = document.querySelector('[data-member-price-selector-slot]');

      // Existing members do not see the membership upsell.
      this.hasMembershipOption = !!el.querySelector('[data-membership-upsell-checkbox], .purchase-option--membership');

      this.radios = el.querySelectorAll('.purchase-option__radio');
      this.labels = el.querySelectorAll('.purchase-option');
      this.benefitsPanel = el.querySelector('.purchase-option__benefits');
      this.membershipUpsell = el.querySelector('[data-membership-upsell]');
      this.membershipCheckbox = el.querySelector('[data-membership-upsell-checkbox]');
      this.membershipDisclosure = el.querySelector('[data-membership-disclosure]');
      this.benefitsModal = el.querySelector('[data-membership-benefits-modal]');
      this.benefitsOpen = el.querySelector('[data-membership-benefits-open]');
      this.benefitsClose = el.querySelectorAll('[data-membership-benefits-close]');
      this.atcButton = el.querySelector('.purchase-option__atc');
      this.atcText = el.querySelector('.purchase-option__atc-text');
      this.quantityInput = el.querySelector('.quantity__input');
      this.lastFocusedElement = null;

      this.bindRadios();
      this.bindMembershipUpsell();
      this.bindBenefitsModal();
      this.bindQuantityChange();
      this.bindVariantChange();
      this.interceptSubmit();
      this.moveMemberPriceSelector();
      this.syncMemberPricing();
      this.updateSelectedState();
      this.updateAtcText();
    }

    bindRadios() {
      this.radios.forEach((radio) => {
        radio.addEventListener('change', () => {
          this.selectedOption = radio.value;
          this.updateSelectedState();
          this.updateAtcText();
        });
      });
    }

    moveMemberPriceSelector() {
      if (!this.memberPriceSlot) return;
      if (this.memberPriceOptions) this.memberPriceSlot.appendChild(this.memberPriceOptions);
      if (this.signedInMemberPrice) this.memberPriceSlot.appendChild(this.signedInMemberPrice);
    }

    bindMembershipUpsell() {
      if (!this.membershipCheckbox) return;
      this.membershipCheckbox.addEventListener('change', () => {
        this.selectedOption = this.membershipCheckbox.checked ? 'membership' : 'onetime';
        this.updateSelectedState();
        this.updateAtcText();
      });
    }

    bindBenefitsModal() {
      if (!this.benefitsModal || !this.benefitsOpen) return;

      this.benefitsOpen.addEventListener('click', () => this.openBenefitsModal());
      this.benefitsClose.forEach((control) => {
        control.addEventListener('click', () => this.closeBenefitsModal());
      });

      this.onBenefitsKeydown = (event) => {
        if (event.key === 'Escape' && !this.benefitsModal.hidden) {
          this.closeBenefitsModal();
          return;
        }

        if (event.key === 'Tab' && !this.benefitsModal.hidden) {
          this.trapBenefitsFocus(event);
        }
      };
      document.addEventListener('keydown', this.onBenefitsKeydown);
    }

    trapBenefitsFocus(event) {
      const focusable = this.benefitsModal.querySelectorAll(
        'a[href], button:not([disabled]):not([tabindex="-1"]), textarea, input, select, [tabindex]:not([tabindex="-1"])'
      );
      if (!focusable.length) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    openBenefitsModal() {
      if (!this.benefitsModal) return;
      this.lastFocusedElement = document.activeElement;
      this.benefitsModal.hidden = false;
      this.benefitsModal.classList.add('membership-benefits-modal--open');
      document.documentElement.classList.add('membership-benefits-modal-is-open');

      const closeButton = this.benefitsModal.querySelector('.membership-benefits-modal__close');
      if (closeButton) closeButton.focus();
    }

    closeBenefitsModal() {
      if (!this.benefitsModal) return;
      this.benefitsModal.classList.remove('membership-benefits-modal--open');
      this.benefitsModal.hidden = true;
      document.documentElement.classList.remove('membership-benefits-modal-is-open');

      if (this.lastFocusedElement && typeof this.lastFocusedElement.focus === 'function') {
        this.lastFocusedElement.focus();
      }
    }

    updateSelectedState() {
      this.labels.forEach((label) => {
        const r = label.querySelector('.purchase-option__radio');
        if (r) label.classList.toggle('purchase-option--selected', r.value === this.selectedOption);
      });

      if (this.membershipCheckbox) {
        this.membershipCheckbox.checked = this.selectedOption === 'membership';
      }

      if (this.membershipUpsell) {
        this.membershipUpsell.classList.toggle('membership-upsell--selected', this.selectedOption === 'membership');
      }

      if (this.membershipDisclosure) {
        this.membershipDisclosure.hidden = this.selectedOption !== 'membership';
      }

      if (this.benefitsPanel) {
        const show = this.selectedOption === 'membership';
        this.benefitsPanel.setAttribute('aria-hidden', String(!show));
        this.benefitsPanel.classList.toggle('purchase-option__benefits--visible', show);
      }
    }

    bindQuantityChange() {
      if (!this.quantityInput) return;
      this.quantityInput.addEventListener('change', () => {
        this.syncMemberPricing();
        this.updateAtcText();
      });
      this.quantityInput.addEventListener('input', () => {
        this.syncMemberPricing();
        this.updateAtcText();
      });
      // Also watch stepper button clicks via MutationObserver on value
      const observer = new MutationObserver(() => this.updateAtcText());
      observer.observe(this.quantityInput, { attributes: true, attributeFilter: ['value'] });
      // Stepper buttons fire input events
      this.el.querySelectorAll('.quantity__button').forEach(btn => {
        btn.addEventListener('click', () => setTimeout(() => {
          this.syncMemberPricing();
          this.updateAtcText();
        }, 0));
      });
    }

    getQuantity() {
      return this.quantityInput ? parseInt(this.quantityInput.value, 10) || 1 : 1;
    }

    updateAtcText() {
      if (!this.atcText) return;
      const qty = this.getQuantity();
      const isMembership = this.selectedOption === 'membership';
      const purchaseTarget = resolvePurchaseTarget(this.el);
      const selectedVariant = resolveSelectedVariant(this.el);
      const preorderState = purchaseTarget.isVirtualBundle
        ? resolveVirtualBundlePreorderState(this.el, purchaseTarget.variantId)
        : resolveVariantPreorderState(this.el, selectedVariant);
      const form = this.el.querySelector('.form[data-type="add-to-cart-form"]');
      const isPurpleDotManaged = form?.classList.contains('x-pd-atc-form');
      const isPurpleDotPreorder = form?.classList.contains('x-pd-atc-form--preorder');
      const preorderTimeline = this.el.querySelector('[data-custom-preorder-timeline]');
      if (preorderTimeline) {
        preorderTimeline.hidden = isPurpleDotManaged || !preorderState.active;
        if (!isPurpleDotManaged && preorderState.timeline) preorderTimeline.innerHTML = preorderState.timeline;
      }
      this.el.dataset.inventorySoldOut = String(purchaseTarget.isSoldOut);
      this.el.classList.toggle('product-purchase-options--preorder', isPurpleDotPreorder || purchaseTarget.isPreorder);
      window.updateEpicBedsShipDate?.(purchaseTarget.variantId);

      // Once Purple Dot has claimed the form, it is the source of truth for CTA
      // text, disabled state, submit behavior, and preorder metadata.
      if (isPurpleDotManaged) return;

      if (purchaseTarget.isSoldOut) {
        this.atcText.textContent = 'Notify Me When Available';
        this.atcButton?.setAttribute('type', 'button');
        this.atcButton?.removeAttribute('name');
        this.atcButton?.setAttribute('data-modal-target', `back-in-stock-modal-${this.sectionId}`);
        if (this.atcButton) {
          this.atcButton.disabled = false;
          this.atcButton.classList.remove('button--primary');
          this.atcButton.classList.add('button--secondary', 'bis-notify-button');
        }
        return;
      }

      if (this.atcButton) {
        this.atcButton.disabled = false;
        this.atcButton.setAttribute('type', 'submit');
        this.atcButton.setAttribute('name', 'add');
      }

      // If button is OOS/notify state, never overwrite the text
      if (this.atcButton && this.atcButton.hasAttribute('data-modal-target')) return;

      if (purchaseTarget.isPreorder) {
        this.atcText.textContent = isMembership ? 'Pre-Order + Membership' : 'Pre-Order';
        return;
      }

      if (isMembership) {
        this.atcText.textContent = qty > 1 ? `Add ${qty} to Cart + Membership` : 'Add to Cart + Membership';
      } else {
        this.atcText.textContent = qty > 1 ? `Add ${qty} to Cart` : 'Add to Cart';
      }
    }

    bindVariantChange() {
      const bundleRoot = document.querySelector('.bundles-container');
      if (!bundleRoot) return;
      bundleRoot.addEventListener('change', (e) => {
        if (e.target.name === 'option1' || e.target.name === 'option2') {
          const selectedVariant = resolveSelectedVariant(this.el);
          const variantInput = this.el.querySelector('.product-variant-id');
          if (selectedVariant && variantInput) {
            variantInput.value = selectedVariant.id;
            variantInput.disabled = selectedVariant.available === false;
          }
          this.syncPrices();
          this.syncMemberPricing();
          this.updateAtcText();
        }
      });
    }

    syncMemberPricing() {
      if (!this.memberPriceConfig) return;
      const purchaseTarget = resolvePurchaseTarget(this.el);
      const variant = this.memberPriceConfig.variants?.find((item) => String(item.id) === String(purchaseTarget.variantId));
      if (!variant) return;
      const selectorElements = (selector) => [this.el, this.memberPriceSlot]
        .filter(Boolean)
        .flatMap((root) => Array.from(root.querySelectorAll(selector)));
      const quantity = Math.max(1, this.getQuantity());
      const multiplier = Math.max(1, Number(purchaseTarget.quantityMultiplier) || 1);
      const effectiveQuantity = quantity * multiplier;
      const memberPrice = resolveMemberPrice(this.memberPriceConfig, variant, effectiveQuantity);
      const onetimePrice = resolveNonMemberPrice(this.memberPriceConfig, variant, effectiveQuantity)
        ?? variant.price * effectiveQuantity;
      const displayMemberPrice = memberPrice
        ? (memberPrice.isLineTotal ? memberPrice.price : memberPrice.price * multiplier)
        : onetimePrice;
      const hasMemberPrice = !this.memberPriceConfig.isMembershipProduct && displayMemberPrice < onetimePrice;

      selectorElements('[data-member-price-display]').forEach((element) => {
        element.textContent = formatMoney(displayMemberPrice);
      });
      selectorElements('[data-onetime-price-display]').forEach((element) => {
        element.textContent = formatMoney(onetimePrice);
      });
      if (this.memberPriceOptions) {
        this.memberPriceOptions.hidden = this.memberPriceConfig.isMember || !hasMemberPrice;
      }
      if (this.signedInMemberPrice) {
        this.signedInMemberPrice.hidden = !this.memberPriceConfig.isMember || !hasMemberPrice;
      }
      if ((this.memberPriceConfig.isMember || !hasMemberPrice) && this.selectedOption === 'membership') {
        this.selectedOption = 'onetime';
        this.updateSelectedState();
      }
    }

    syncPrices() {
      const bundleRoot = document.querySelector('.bundles-container');
      const variantsJsonEl = document.getElementById('product-variants-json');
      if (!bundleRoot || !variantsJsonEl) return;
      try {
        const variants = JSON.parse(variantsJsonEl.textContent);
        const o1 = bundleRoot.querySelector('input[name="option1"]:checked');
        const o2 = bundleRoot.querySelector('input[name="option2"]:checked');
        let v = null;
        if (o1 && o2) v = variants.find(x => x.option1 === o1.value && x.option2 === o2.value);
        else if (o1) v = variants.find(x => x.option1 === o1.value);
        if (!v) return;

        const reg = v.price;
        const mem = Math.round(reg * 0.9);
        const fmt = (c) => '$' + (c / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');

        const onetimeEl = this.el.querySelector('[data-onetime-price]');
        const compareEl = this.el.querySelector('[data-compare-price]');
        const memberEl = this.el.querySelector('[data-member-price]');
        const savingsEl = this.el.querySelector('[data-membership-savings]');
        if (onetimeEl) onetimeEl.textContent = fmt(reg);
        if (compareEl) compareEl.textContent = fmt(reg);
        if (memberEl) memberEl.textContent = fmt(mem);
        if (savingsEl) savingsEl.textContent = fmt(reg - mem);
      } catch (e) {}
    }

    interceptSubmit() {
      const form = this.el.querySelector('.form[data-type="add-to-cart-form"]');
      if (!form) return;
      // Capture phase so we run before bundle-picker's bubble listener
      form.addEventListener('submit', (e) => {
        e.preventDefault();
        e.stopImmediatePropagation();
        if (this.atcButton?.dataset.egAtcState === 'pending') return;
        if (this.selectedOption === 'membership') {
          this.addWithMembership();
        } else {
          this.addOneTime();
        }
      }, true);
    }

    setLoading(on) {
      if (!this.atcButton) return;
      on ? this.atcButton.setAttribute('disabled', 'disabled') : this.atcButton.removeAttribute('disabled');
      this.atcButton.classList.toggle('loading', on);
      this.atcButton.setAttribute('aria-busy', String(on));
      window.EGAtcFeedback?.[on ? 'pending' : 'idle'](this.atcButton);
      this.atcButton.querySelector('.loading__spinner, .loading-overlay__spinner')?.classList.toggle('hidden', !on);

      document.querySelectorAll('.pdp-sticky-selector .sticky-atc:not(.sticky-atc--early-access)').forEach((button) => {
        button.disabled = on;
        button.classList.toggle('loading', on);
        button.setAttribute('aria-busy', String(on));
        window.EGAtcFeedback?.[on ? 'pending' : 'idle'](button);
        button.querySelector('.loading__spinner, .loading-overlay__spinner')?.classList.toggle('hidden', !on);
      });
    }

    showAddedFeedback() {
      window.EGAtcFeedback?.success(this.atcButton);
      document.querySelectorAll('.pdp-sticky-selector .sticky-atc:not(.sticky-atc--early-access)').forEach(button => window.EGAtcFeedback?.success(button));
    }

    async addOneTime() {
      const purchaseTarget = resolvePurchaseTarget(this.el);
      const variantId = purchaseTarget.variantId;
      if (!variantId) return alert('Could not find selected variant. Please try again.');
      const quantity = this.getQuantity() * purchaseTarget.quantityMultiplier;
      const properties = resolveLineItemProperties(this.el);
      this.setLoading(true);
      try {
        const r = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            items: await withRegiosBedGift([{
              id: variantId,
              quantity,
              ...(Object.keys(properties).length ? { properties } : {}),
            }], this.el),
            sections: this.cfg.drawerSections,
            sections_url: window.location.pathname,
          }),
        });
        if (!r.ok) { const e = await r.json(); throw new Error(e.description || 'Failed'); }
        const result = await r.json();
        if (!renderDrawer(result, variantId)) await refreshDrawer(this.cfg.drawerSections, variantId);
        this.showAddedFeedback();
      } catch (err) {
        console.error('[PurchaseOptions] One-time add failed:', err);
        window.EGAtcFeedback?.error(this.atcButton, err.message || 'Unable to add to cart. Please try again.');
      } finally {
        this.setLoading(false);
      }
    }

    async addWithMembership() {
      const purchaseTarget = resolvePurchaseTarget(this.el);
      const variantId = purchaseTarget.variantId;
      if (!variantId) return alert('Could not find selected variant. Please try again.');

      const cfg = this.cfg;
      if (!cfg.membershipVariantId) return alert('Membership product not found. Please try again.');

      const quantity = this.getQuantity() * purchaseTarget.quantityMultiplier;
      const properties = resolveLineItemProperties(this.el);
      this.setLoading(true);

      try {
        // Check current cart to avoid duplicate membership or gift
        const cart = await getFreshCart();

        const hasMembership = cart.items.some(item =>
          (item.variant_id === cfg.membershipVariantId &&
            item.selling_plan_allocation?.selling_plan?.id == cfg.sellingPlanId)
          || (cfg.membershipItemVariantId && item.variant_id === cfg.membershipItemVariantId && !item.selling_plan_allocation)
        );
        if (hasMembership) {
          // Membership already in cart -- just add the bed
          await this.addOneTime();
          return;
        }

        // The membership coordinator owns membership and welcome-gift writes.
        // Add the selected product first so Purple Dot properties are present
        // when it chooses recurring versus one-time membership.
        if (typeof window.EGAddMembershipToCart === 'function') {
          const productResponse = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              items: await withRegiosBedGift([{
                id: variantId,
                quantity,
                ...(Object.keys(properties).length ? { properties } : {}),
              }], this.el)
            }),
          });

          if (!productResponse.ok) {
            const error = await productResponse.json();
            throw new Error(error.description || 'Failed');
          }

          await window.EGAddMembershipToCart(variantId);
          this.showAddedFeedback();
          return;
        }

        const hasGift = cart.items.some(item =>
          item.variant_id?.toString() === cfg.giftVariantId.toString() &&
          item.properties?.[cfg.giftPropertyKey] === cfg.giftPropertyValue
        );

        const items = await withRegiosBedGift([
          {
            id: variantId,
            quantity,
            ...(Object.keys(properties).length ? { properties } : {}),
          },
          {
            id: this.cartHasPreorder(cart) && cfg.membershipItemVariantId
              ? cfg.membershipItemVariantId
              : cfg.membershipVariantId,
            quantity: 1,
            ...(this.cartHasPreorder(cart) && cfg.membershipItemVariantId ? {} : { selling_plan: cfg.sellingPlanId })
          },
          ...(hasGift ? [] : [{
            id: cfg.giftVariantId,
            quantity: 1,
            properties: {
              [cfg.giftPropertyKey]: cfg.giftPropertyValue,
              _linked_to_product: cfg.membershipProductId,
            },
          }]),
        ], this.el);

        const r = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            items,
            sections: cfg.drawerSections,
            sections_url: window.location.pathname,
          }),
        });

        if (!r.ok) { const e = await r.json(); throw new Error(e.description || 'Failed'); }
        const result = await r.json();

        if (!renderDrawer(result, variantId)) await refreshDrawer(cfg.drawerSections, variantId);
        this.showAddedFeedback();

        // Dispatch the same event cart-drawer-membership.js listens to so its UI stays in sync
        document.dispatchEvent(new CustomEvent('membership:added', { bubbles: true }));

      } catch (err) {
        console.error('[PurchaseOptions] Membership add failed:', err);
        window.EGAtcFeedback?.error(this.atcButton, err.message || 'Unable to add to cart. Please try again.');
      } finally {
        this.setLoading(false);
      }
    }

    cartHasPreorder(cart) {
      return Boolean(cart?.items?.some((item) => Object.entries(item.properties || {}).some(([key, value]) => {
        const normalizedKey = String(key).toLowerCase();
        const normalizedValue = String(value || '').toLowerCase();
        return normalizedKey.includes('purple dot')
          || normalizedKey.includes('pre-order')
          || normalizedKey.includes('preorder')
          || normalizedValue.includes('purple dot')
          || normalizedValue.includes('pre-order')
          || normalizedValue.includes('preorder');
      })));
    }
  }

  function initAll() {
    document.querySelectorAll('.product-purchase-options').forEach((el) => {
      if (!el.dataset.purchaseOptionsInit) {
        el.dataset.purchaseOptionsInit = '1';
        new ProductPurchaseOptions(el);
      }
    });
  }

  document.addEventListener('DOMContentLoaded', initAll);
  document.addEventListener('shopify:section:load', initAll);
  document.addEventListener('DOMContentLoaded', bindSetupUpsells);
  document.addEventListener('shopify:section:load', bindSetupUpsells);
})();
